"use client"

import * as React from "react"
import { AppIcon } from "@/components/desk/AppIcon"
import { App } from "@/types/workspace"

interface AuthAppConsentModalProps {
  authAppToShow: any | null
  onClose: () => void
  onAuthorize: (app: any) => Promise<void>
}

export function AuthAppConsentModal({
  authAppToShow,
  onClose,
  onAuthorize
}: AuthAppConsentModalProps) {
  if (!authAppToShow) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200 font-sans">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 font-sans">
        {/* Header: Google Auth Style */}
        <div className="p-6 pb-4 flex flex-col items-center text-center border-b border-zinc-100 dark:border-zinc-800/80">
          <div className="flex items-center gap-1.5 mb-4 select-none">
            <span className="text-[#004EB1] dark:text-blue-400 font-black text-xl tracking-tight font-sans">Bold Connect</span>
          </div>

          <div className="flex items-center gap-4 my-2">
            <div className="size-12 rounded-xl bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center border border-zinc-200 dark:border-zinc-800 p-1.5 shadow-sm">
              <AppIcon iconUrl={authAppToShow.iconUrl} iframeUrl={authAppToShow.iframeUrl} name={authAppToShow.name} size="medium" />
            </div>
          </div>

          <h3 className="text-base font-bold text-zinc-900 dark:text-white mt-2">
            Permitir que o aplicativo {authAppToShow.name} acesse a sua conta?
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            Ao autorizar, este aplicativo poderá interagir com os seus recursos e dados do Workspace.
          </p>
        </div>

        {/* Content: Permissions requested (Scopes) */}
        <div className="p-6 space-y-4 max-h-[300px] overflow-y-auto">
          <div className="space-y-2">
            <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
              Recursos que este app deseja acessar:
            </span>
            
            <div className="space-y-2 bg-zinc-50 dark:bg-zinc-950/40 rounded-xl p-3.5 border border-zinc-100 dark:border-zinc-850">
              {authAppToShow.scopes && authAppToShow.scopes.length > 0 ? (
                authAppToShow.scopes.map((scope: string) => {
                  let scopeLabel = scope
                  let scopeDesc = ""
                  if (scope === "read:contacts") {
                    scopeLabel = "Visualizar contatos"
                    scopeDesc = "Permite ler dados da sua agenda de contatos corporativos."
                  } else if (scope === "write:contacts") {
                    scopeLabel = "Gerenciar contatos"
                    scopeDesc = "Permite adicionar, atualizar e remover contatos."
                  } else if (scope === "read:finance") {
                    scopeLabel = "Visualizar dados financeiros"
                    scopeDesc = "Acesso de leitura a faturamentos, boletos e fluxo de caixa."
                  } else if (scope === "write:finance") {
                    scopeLabel = "Gerenciar fluxos financeiros"
                    scopeDesc = "Permite criar lançamentos e faturas no sistema financeiro."
                  } else if (scope === "read:analytics") {
                    scopeLabel = "Visualizar relatórios de BI"
                    scopeDesc = "Acesso aos dados analíticos do painel Bold Analytics."
                  } else if (scope === "admin") {
                    scopeLabel = "Acesso Administrativo Completo"
                    scopeDesc = "Acesso total a todas as configurações deste workspace."
                  }

                  return (
                    <div key={scope} className="flex gap-2.5 items-start text-xs font-sans text-zinc-700 dark:text-zinc-300">
                      <div className="size-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                      <div>
                        <p className="font-bold text-zinc-800 dark:text-zinc-200">{scopeLabel}</p>
                        {scopeDesc && <p className="text-[10px] text-zinc-450 dark:text-zinc-500 mt-0.5 leading-relaxed">{scopeDesc}</p>}
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="flex gap-2.5 items-start text-xs font-sans text-zinc-700 dark:text-zinc-300">
                  <div className="size-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <div>
                    <p className="font-bold text-zinc-800 dark:text-zinc-200">Acesso básico à conta</p>
                    <p className="text-[10px] text-zinc-450 dark:text-zinc-500 mt-0.5 leading-relaxed">Permite identificar a sua sessão e carregar as configurações do app.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dependencies section */}
          {authAppToShow.slug === "bold-crm" && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3.5 space-y-1.5 animate-in fade-in duration-300">
              <h4 className="text-xs font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1.5 font-sans">
                <span>⚠️ Requisito de Dependência</span>
              </h4>
              <p className="text-[10px] text-zinc-600 dark:text-zinc-400 leading-normal font-sans">
                O <strong>Bold CRM</strong> requer o aplicativo <strong>Bold Contatos</strong>. 
                Ao prosseguir, ambos os aplicativos serão instalados e habilitados automaticamente no seu workspace.
              </p>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="bg-zinc-50 dark:bg-zinc-950 p-4 px-6 border-t border-zinc-100 dark:border-zinc-800/80 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-850 cursor-pointer transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onAuthorize(authAppToShow)}
            className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 cursor-pointer shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 transition-all"
          >
            Autorizar e Instalar
          </button>
        </div>
      </div>
    </div>
  )
}
